import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { catalogAdmin } from '../api/index.js';
import { Badge, statusBadge } from '../components/ui/badge.js';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card.js';
import { Button } from '../components/ui/button.js';
import { Loader2, AlertCircle, Pencil, X, Check, UserCircle } from 'lucide-react';
import { format } from 'date-fns';
import { useEnableEdit } from '../hooks/useAuth.js';

const SENTINEL = 'ig.field.type.delete';

function sv(v: unknown): string {
  if (!v || v === SENTINEL) return '';
  return String(v);
}

function fmtDate(v: unknown): string {
  const s = sv(v);
  if (!s) return '';
  try { return format(new Date(s), 'MMM d, yyyy'); } catch { return s; }
}

function imgUrl(v: unknown): string | undefined {
  if (!v || v === SENTINEL) return undefined;
  if (typeof v === 'string') return v || undefined;
  if (typeof v === 'object') {
    const o = v as any;
    return o.url ?? o.src ?? o.href ?? undefined;
  }
  return undefined;
}

const INPUT_CLS = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500';
const TEXTAREA_CLS = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 min-h-[80px] resize-y';

function Row({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div className="flex gap-4 py-1.5 text-sm border-b border-gray-50 last:border-0">
      <span className="w-28 text-gray-500 flex-shrink-0">{label}</span>
      <span className="text-gray-900 whitespace-pre-wrap break-words min-w-0">{value}</span>
    </div>
  );
}

interface ContributorForm {
  name: string;
  bio: string;
  links: string;
  imageUrl: string;
  isArchived: boolean;
}

export function ContributorDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const canEdit = useEnableEdit();
  const [editing, setEditing] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState('');
  const [form, setForm] = useState<ContributorForm>({ name: '', bio: '', links: '', imageUrl: '', isArchived: false });

  const { data: contributor, isLoading, error } = useQuery({
    queryKey: ['contributor-detail', id],
    queryFn: () => catalogAdmin.contributorDetail(id!),
    enabled: !!id,
  });

  const { data: appearances } = useQuery({
    queryKey: ['contributor-appearances', id],
    queryFn: () => catalogAdmin.contributorAppearances(id!),
    enabled: !!id && !!contributor,
    staleTime: 2 * 60 * 1000,
  });

  useEffect(() => {
    if (contributor) {
      setForm({
        name: sv(contributor.name),
        bio: sv(contributor.bio),
        links: sv((contributor as any).links),
        imageUrl: imgUrl(contributor.image) ?? '',
        isArchived: Boolean((contributor as any).isArchived),
      });
    }
  }, [contributor]);

  async function handleSave() {
    setSaveStatus('saving');
    setSaveError('');
    try {
      await catalogAdmin.updateContributor(id!, {
        name: form.name,
        bio: form.bio,
        links: form.links || SENTINEL,
        image: form.imageUrl || SENTINEL,
        isArchived: form.isArchived,
      });
      await qc.invalidateQueries({ queryKey: ['contributor-detail', id] });
      await qc.invalidateQueries({ queryKey: ['catalog-contributors'] });
      setSaveStatus('saved');
      setEditing(false);
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (err: unknown) {
      setSaveStatus('error');
      setSaveError(err instanceof Error ? err.message : 'Unknown error');
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        <Loader2 className="animate-spin mr-2" /> Loading…
      </div>
    );
  }

  if (error || !contributor) {
    return (
      <div className="p-8 flex items-center gap-3 text-red-600">
        <AlertCircle size={20} />
        <p>{error instanceof Error ? error.message : 'Contributor not found'}</p>
      </div>
    );
  }

  const image = imgUrl(contributor.image);

  return (
    <div className="p-8 max-w-2xl mx-auto space-y-6">
      <div>
        <Link to="/contributors" className="text-sm text-brand-600 hover:underline mb-2 inline-block">← Back to Cast & Crew</Link>
        <div className="flex items-center gap-4">
          {image ? (
            <img src={image} alt="" className="w-16 h-16 rounded-full object-cover border border-gray-200"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          ) : (
            <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center">
              <UserCircle size={32} className="text-gray-400" />
            </div>
          )}
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">{sv(contributor.name) || '(no name)'}</h1>
            <p className="text-xs text-gray-400 font-mono mt-0.5">{id}</p>
          </div>
          {!editing && canEdit && (
            <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
              <Pencil size={14} /> Edit
            </Button>
          )}
        </div>
      </div>

      {editing ? (
        <Card>
          <CardHeader><CardTitle>Edit Contributor</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
              <input className={INPUT_CLS} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
              <textarea className={TEXTAREA_CLS} value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Image URL</label>
              <input
                className={INPUT_CLS}
                type="url"
                value={form.imageUrl}
                onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))}
                placeholder="https://..."
              />
              {form.imageUrl && (
                <img src={form.imageUrl} alt="" className="mt-2 w-16 h-16 rounded-full object-cover border border-gray-200"
                  onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Links</label>
              <input className={INPUT_CLS} value={form.links} placeholder="https://…" onChange={e => setForm(f => ({ ...f, links: e.target.value }))} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer text-sm">
              <input type="checkbox" checked={form.isArchived}
                onChange={e => setForm(f => ({ ...f, isArchived: e.target.checked }))}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500" />
              <span className="font-medium text-gray-700">Archived</span>
            </label>
            {saveStatus === 'error' && <p className="text-sm text-red-600">{saveError}</p>}
            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => { setEditing(false); setSaveStatus('idle'); }}>
                <X size={14} /> Cancel
              </Button>
              <Button onClick={handleSave} loading={saveStatus === 'saving'}>
                <Check size={14} /> Save
              </Button>
            </div>
            {saveStatus === 'saved' && <p className="text-sm text-green-600 text-right">Saved ✓</p>}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader><CardTitle>Details</CardTitle></CardHeader>
          <CardContent className="space-y-0">
            <Row label="Name" value={sv(contributor.name)} />
            <Row label="Bio" value={sv(contributor.bio)} />
            <Row label="Links" value={sv((contributor as any).links)} />
            <Row label="Archived" value={(contributor as any).isArchived ? 'Yes' : ''} />
            <Row label="Created" value={fmtDate((contributor as any).createdAt)} />
            <Row label="Updated" value={fmtDate((contributor as any).updatedAt)} />
          </CardContent>
        </Card>
      )}

      {/* Appears In — Items */}
      {(appearances?.items?.length ?? 0) > 0 && (
        <Card>
          <CardHeader><CardTitle>Appears In — Items ({appearances!.items.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-xs font-medium text-gray-400 uppercase pb-2">Title</th>
                    <th className="text-left text-xs font-medium text-gray-400 uppercase pb-2">Status</th>
                    <th className="text-left text-xs font-medium text-gray-400 uppercase pb-2">Release</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {appearances!.items.map(item => {
                    const rel = item.releaseAt ? (() => { try { return format(new Date(item.releaseAt!), 'MMM d, yyyy'); } catch { return item.releaseAt!; } })() : '—';
                    return (
                      <tr
                        key={item._id}
                        onClick={() => navigate(`/catalog/${item._id}`)}
                        className="hover:bg-gray-50 cursor-pointer transition-colors"
                      >
                        <td className="py-2 pr-4 font-medium text-gray-900 truncate max-w-[14rem]">{item.name}</td>
                        <td className="py-2 pr-4">
                          {item.status ? <Badge variant={statusBadge(item.status)}>{item.status}</Badge> : '—'}
                        </td>
                        <td className="py-2 text-gray-500 text-xs whitespace-nowrap">{rel}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Appears In — Series */}
      {(appearances?.series?.length ?? 0) > 0 && (
        <Card>
          <CardHeader><CardTitle>Appears In — Series ({appearances!.series.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-1">
              {appearances!.series.map(s => (
                <div
                  key={s._id}
                  onClick={() => navigate(`/series/${s._id}`)}
                  className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0 cursor-pointer hover:bg-gray-50 rounded px-2"
                >
                  <span className="text-sm text-gray-900">{s.name}</span>
                  {s.status && <Badge variant={statusBadge(s.status)}>{s.status}</Badge>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
